import { defineStore } from 'pinia';
import { useModuleStore } from './moduleStore';
import { useBookingSystemStore } from './bookingSystemStore';
import { useAuthAdminStore } from './authAdminStore';
import AdminService from '@/services/admin.service';
import { useInscriptionVIPStore } from './inscriptionVIPStore';
import { useNotificationStore } from './notificationStore';
import { useAuthUserStore } from './authUserStore';
import { useChampsFormulaireStore } from './champsFormulaireStore';




export const useMainStore = defineStore('main', {
    state: () => ({
        module: useModuleStore(),
        champs_formulaire: useChampsFormulaireStore(),
        bookingSystem: useBookingSystemStore(),
        authAdmin: useAuthAdminStore(),
        authUser: useAuthUserStore(),
        billetterie_mode: false,
        parcours_billetterie_mode: false,
        dossard_bourse_mode: false,
        view_as_admin: false,
        inscription_as_admin: false,
        inscription_vip: useInscriptionVIPStore(),
        notification: useNotificationStore(),
        loading: false,

    }),
    getters: {
        is_default_mode() {
            return !this.billetterie_mode && !this.parcours_billetterie_mode;
        },
        is_inscription_vip() {
            if (!this.inscription_vip) { return false; }
            return this.inscription_vip.is_valid();
        }
    },
    actions: {


        /**
         * Initializes the application by setting the module base, fetching data, and 
         * checking for specific metadata to set the parcours billetterie mode.
         * 
         * @param {string} [base=""] - The base URL or path for the module.
         * @returns {Promise<void>} - A promise that resolves when the initialization is complete.
         */
        async initializeApp(base = "") {
            this.loading = true;
            this.dossard_bourse_mode = false;
            this.setModuleBase(base);
            await this.module.fetchData();
            this.setAppStyle();

            if (this.module.metadatas?.module_parcours_billetterie == "1") {
                this.parcours_billetterie_mode = true;
            }
            if (this.module.metadatas?.module_billetterie == '1') {
                this.billetterie_mode = true;
            }
            this.loading = false;
        },

        /**
         * Initializes the view as admin, to check module even when closed.
         * 
         * This method checks if the current user has admin rights and sets the view accordingly.
         * If the user is a Sportips admin with super admin rights, the view is set to admin.
         * Otherwise, it validates the provided token to determine if the view should be set to admin.
         * 
         * @param {string} token - The token to validate for admin view.
         * @returns {Promise<void>} A promise that resolves when the view is set.
         */
        async init_view_as_admin(token) {
            if (this.authAdmin.isSportipsAdmin && this.authAdmin.hasSuperAdminRights) {
                this.view_as_admin = true;
                return;
            }
            let res = await AdminService.validTokenViewAsAdmin(token, this.module.base);
            this.view_as_admin = res;

        },

        /**
         * Initializes the inscription process as an admin.
         * 
         * It validates the provided token to determine if the user can view as an admin.
         * 
         * @param {string} token - The token to validate admin rights.
         * @returns {Promise<void>} - A promise that resolves when the initialization is complete.
         */
        async init_inscription_as_admin(token) {
            /* if (this.authAdmin.isSportipsAdmin && this.authAdmin.hasSuperAdminRights) {
                this.inscription_as_admin = true;
                return;
            } */
            let res = await AdminService.validTokenViewAsAdmin(token, this.module.base);
            this.inscription_as_admin = res;
        },

        async init_inscription_vip(token) {
            if (token == undefined) return;
            this.inscription_vip.token = token;
            this.inscription_vip.id_module = this.module.id;
            await this.inscription_vip.fetch();
        },

        async init_inscription_vip_multi_usage(token) {
            if (token == undefined) return;
            this.inscription_vip.token = token;
            this.inscription_vip.id_module = this.module.id;
            await this.inscription_vip.fetch_multi_usage();
        },


        /**
         * Sets the module base and updates the billetterie_mode flag.
         * If the base string includes "BILLETTERIE_", it sets billetterie_mode to true,
         * removes the "BILLETTERIE_" prefix from the base string, and assigns the result to this.module.base.
         * Otherwise, it sets billetterie_mode to false and assigns the base string directly to this.module.base.
         *
         * @param {string} base - The base string to set for the module.
         */
        setModuleBase(base) {
            if (base.includes("BILLETTERIE_")) {
                this.billetterie_mode = true;
                const base_module = base.replace("BILLETTERIE_", "");
                this.module.base = base_module;
            }
            else {
                this.billetterie_mode = false;
                this.module.base = base
            }
        },


        /**
         * Sets the background style of the application.
         * If the module's `url_background` is not an empty string,
         * it sets the background of the first element with the class "v-application"
         * to the specified URL.
         */
        setAppStyle() {
            if (this.module.url_background != '') {
                if (document.getElementsByClassName("v-application")[0]) {
                    document.getElementsByClassName("v-application")[0].style.background = "url('" + this.module.url_background + "')";
                }

            }
        },


    },
});